import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';
import { DEFAULT_REPORT_TAB, DELIVERY_REPORT_TABS } from '@/entities/delivery-report';
import { useLegacyMode } from '@/entities/legacy-mode';
import { DeliveryReportsPage } from '@/pages/delivery-reports';
import { LegacyReportsPage } from '@/pages/legacy-reports';
import { requireAuth } from '../require-auth';

/**
 * URL state for the Отчеты screen. The active `tab` is the source of truth;
 * the delivery-log tab additionally scopes by `branch` (branch selector) and
 * `from`/`to` (the week range written by the toolbar's date picker). All
 * optional/defaulted.
 *
 * `page`, `limit` and `start_date`/`end_date` are legacy-only — the old
 * reports screen is a paginated table (numbered pager + "Показать по N") whose
 * generation window persists to the URL, with no tabs. Defaulted (not merely
 * `.catch()`ed) so navigating here from elsewhere doesn't have to supply
 * them; see `delivery.orders.tsx` for the same note. `start_date`/`end_date`
 * are separate from the delivery-log tab's `from`/`to` on purpose — the two
 * screens' windows mean different things and must not clobber each other.
 */
const deliveryReportsSearchSchema = z.object({
  tab: z.enum(DELIVERY_REPORT_TABS).catch(DEFAULT_REPORT_TAB),
  branch: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(1).default(1).catch(1),
  limit: z.number().int().min(1).default(15).catch(15),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});

export const Route = createFileRoute('/delivery/reports')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: deliveryReportsSearchSchema,
  component: DeliveryReportsRoute,
});

function DeliveryReportsRoute() {
  const { tab, branch, page, limit, start_date, end_date } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const legacy = useLegacyMode();

  if (legacy) {
    return (
      <LegacyReportsPage
        page={page}
        limit={limit}
        startDate={start_date}
        endDate={end_date}
        onPageChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, page: next }) });
        }}
        // A new page size re-buckets every row, so the pager returns to 1 —
        // keeping the old offset could point past the last page.
        onLimitChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, limit: next, page: 1 }) });
        }}
        onRangeChange={({ start, end }) => {
          void navigate({
            search: (prev) => ({ ...prev, start_date: start, end_date: end }),
          });
        }}
      />
    );
  }

  return (
    <DeliveryReportsPage
      tab={tab}
      branch={branch}
      onTabChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, tab: next }) });
      }}
    />
  );
}
